import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { useThemeMode } from '../../../_myb/partials';
import { getCSSVariableValue } from '../../../_myb/assets/ts/_utils';
import { Content } from '../../../_myb/layout/components/content';
import { ToolbarWrapper } from '../../../_myb/layout/components/toolbar';
import { PageTitle } from '../../../_myb/layout/core';
import { ToolbarContext, defaultToolbarState } from '../../../_myb/layout/components/toolbar/Toolbar';
import { KTIcon } from '../../../_myb/helpers';
import { formatCurrency } from '../../modules/reports/components/ReportHelpers';
import {
  BalancesDTO,
  DashboardResponseV2,
  DateFilter,
  PeriodData,
  ReportFilter,
  SubscriptionData,
} from '../../types';
import {
  fetchAccounts,
  fetchContacts,
  fetchDashboardBalances,
  fetchDashboardCurrentPeriod,
  fetchDashboardPriorPeriod,
  fetchItems,
  fetchSeenFeatures,
  markFeatureSeen,
} from '../../api';
import { brandSettings } from '../../modules/settings/BrandSettings';
import CustomModal from '../../modules/widgets/components/CustomModal';
import { OnboardingHelper } from './OnboardingHelper';
import { MarketingWidget } from './MarketingWidget';
import { useNavigate } from 'react-router-dom';
import { globalValue, useAppContext } from '../../modules/apps/AppContext';
import DonutChart from '../../../_myb/partials/widgets/charts/DonutChart';
import { canAccess } from '../../modules/auth/accessControl';
import { AccessibleTiles } from '../../modules/widgets/components/AccessibleTiles';
import { getEffectiveEdition, canUseModule } from '../../modules/subscription/subscriptionAccess';
import BudgetTrackerWidget from './BudgetTrackerWidget';
import AiStatusBriefCard from './AiStatusBriefCard';
import { useBootstrapTooltips } from '../../modules/utils/useBootstrapTooltips';

// ── Constants ─────────────────────────────────────────────────────────────────

const DATE_OPTIONS = [
  { value: 'ThisYear', label: 'This Year' },
  { value: 'ThisMonth', label: 'This Month' },
  { value: 'ThisQuarter', label: 'This Quarter' },
  { value: 'LastMonth', label: 'Last Month' },
  { value: 'LastQuarter', label: 'Last Quarter' },
  { value: 'LastYear', label: 'Last Year' },
];

// `priority` ranks which unseen tip is surfaced first (lower = shown sooner). Features that
// still require the user to opt in are ranked above features we now auto-enable by default
// (Automatic Payment Reminders, Business Summary Email) — those are informational only now.
const TIPS = [
  {
    key: 'late-fee-system',
    priority: 1,
    icon: 'timer',
    title: 'New: Late Fee System',
    body: 'Automatically calculate late fees on overdue invoices — fixed, percentage or daily interest. Configure it in Settings → Late Fee Settings.',
  },
  {
    key: 'custom-roles',
    priority: 2,
    icon: 'shield-tick',
    title: 'New: Custom Roles & Permissions',
    body: 'Create fine-grained roles with object-level access control. Go to Settings → Users → Custom Roles to get started.',
  },
  {
    key: 'backup-restore',
    priority: 3,
    icon: 'archive-tick',
    title: 'New: Backup & Restore',
    body: 'Export all your business data as a ZIP backup and restore from it anytime. Find it in Settings → Data Management.',
  },
  {
    key: 'period-comparison',
    priority: 4,
    icon: 'chart-line-up',
    title: 'Period Comparison in Reports',
    body: 'P&L, Balance Sheet, Cash Flow and more now show side-by-side prior period columns so you can spot trends instantly.',
  },
  {
    key: 'documents-module',
    priority: 5,
    icon: 'folder',
    title: 'New: Documents Module',
    body: 'Upload, manage and organise your business documents in one place. Track storage usage and attach files to records.',
  },
  {
    key: 'global-search',
    priority: 6,
    icon: 'magnifier',
    title: 'Improved Global Search',
    body: 'The search bar has been fully rewritten — faster results across transactions, contacts, items and more.',
  },
  {
    key: 'recycle-bin',
    priority: 7,
    icon: 'trash',
    title: 'Recycle Bin',
    body: 'Deleted records are kept in the Recycle Bin so you can restore anything you accidentally removed.',
  },
  {
    key: 'transaction-activity-log',
    priority: 8,
    icon: 'notepad-edit',
    title: 'Transaction Activity Log',
    body: 'Open any invoice to see a full log of emails and WhatsApp messages sent, including delivery status per recipient.',
  },
  {
    key: 'save-and-add-new',
    priority: 9,
    icon: 'add-item',
    title: 'Save & Add New',
    body: 'Save a transaction and immediately open a blank new one of the same type with the new "Save & Add New" button on every form.',
  },
  {
    key: 'redesigned-settings',
    priority: 10,
    icon: 'setting',
    title: 'Redesigned Settings',
    body: 'Settings are now grouped by area — Business, Sales, Users, Inventory, Data and more. Everything is easier to find.',
  },
  {
    key: 'auto-payment-reminders',
    priority: 11,
    icon: 'notification',
    title: 'Automatic Payment Reminders',
    body: "We've turned this on for you: overdue invoices now get automatic reminders. Manage the schedule in Settings → Sales & Invoicing → Payment Reminders.",
  },
  {
    key: 'business-summary-email',
    priority: 12,
    icon: 'sms',
    title: 'Business Summary Email',
    body: "We've turned this on for you: a summary of your business performance is now emailed on a schedule. Manage frequency in Settings → Business Summary.",
  },
  {
    key: 'manufacturing-module',
    priority: 13,
    icon: 'setting-4',
    title: 'New: Manufacturing Module',
    body: 'Define finished goods as Bills of Materials and track Work Orders from planning through completion, with stock and accounting updated automatically. Find it under Manufacturing in the sidebar.',
  },
  {
    key: 'projects-module',
    priority: 14,
    icon: 'tablet-book',
    title: 'New: Projects Module',
    body: 'Create projects for a client, tag transactions to them, and generate invoices from billing milestones. Track billing status and project profitability under Projects in the sidebar.',
  },
  {
    key: 'budgets-module',
    priority: 15,
    icon: 'calculator',
    title: 'New: Budgets Module',
    body: 'Set planned income and expense amounts per account for a financial year and compare them against live actuals with the Budget vs Actual report. Find it under Budgets in the sidebar.',
  },
  {
    key: 'customer-portal',
    priority: 16,
    icon: 'monitor-mobile',
    title: 'New: Customer Portal',
    body: 'Give customers a self-service portal to view invoices, pay online and check their account statement, branded with your logo and colours. Enable it in Settings → Customer Portal.',
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function pctChange(current: number, prior: number): number {
  if (prior === 0) return 0;
  return ((current - prior) / Math.abs(prior)) * 100;
}

function formatPeriodLabel(from: string | Date, to: string | Date): string {
  if (!from || !to) return '—';
  const timeZone = globalValue?.bizMaster?.bizDetails?.timeZone || 'UTC';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric', timeZone };
  // The dates are ISO UTC strings representing local midnight / end-of-day in the
  // business's timezone (e.g. IST April 1 00:00 → UTC March 31 18:30).
  // Formatting with an explicit timeZone converts back to the correct month in the
  // business's own timezone regardless of the viewing browser's local timezone —
  // avoiding both the earlier bug (splitting on "T" and taking the UTC date showed
  // March instead of April) and a later one (defaulting to the browser's timezone
  // showed the wrong month for users viewing from a different timezone than the biz).
  const toDisplayDate = (s: string | Date): Date => s instanceof Date ? s : new Date(s);
  const f = toDisplayDate(from).toLocaleDateString('en-US', opts);
  const t = toDisplayDate(to).toLocaleDateString('en-US', opts);
  return f === t ? f : `${f} – ${t}`;
}

function buildReportFilter(predefined: string): ReportFilter {
  const df = new DateFilter();
  df.predefinedDateFilter = predefined;
  df.updateDates();
  return new ReportFilter({ dateFilter: df });
}

/** Compact number formatter for chart y-axis labels (no currency symbol). */
function fmtCompact(v: number): string {
  const abs = Math.abs(v);
  const neg = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${neg}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${neg}${(abs / 1_000).toFixed(1)}K`;
  return `${neg}${abs.toFixed(0)}`;
}

// ── PeriodSelect (top-level filter) ──────────────────────────────────────────

interface PeriodSelectProps {
  value: string;
  onChange: (v: string) => void;
  loading?: boolean;
}

const PeriodSelect: FC<PeriodSelectProps> = ({ value, onChange, loading }) => (
  <div className='d-flex align-items-center gap-2'>
    {loading && (
      <span
        className='spinner-border spinner-border-sm text-primary'
        style={{ width: '14px', height: '14px' }}
      />
    )}
    <select
      className='form-select form-select-sm'
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={loading}
      style={{ minWidth: '120px', width: 'auto' }}
    >
      {DATE_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label.toTranslatedDisplay()}
        </option>
      ))}
    </select>
  </div>
);

// ── CompactTip (inline tip next to dashboard title) ───────────────────────────

const SORTED_TIPS = [...TIPS].sort((a, b) => a.priority - b.priority);

const CompactTip: FC = () => {
  // null while loading (avoids flashing an already-seen tip before the backend responds)
  const [unseenKeys, setUnseenKeys] = useState<string[] | null>(null);
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    let active = true;
    fetchSeenFeatures()
      .then(seen => {
        if (!active) return;
        const seenSet = new Set(seen);
        setUnseenKeys(SORTED_TIPS.filter(t => !seenSet.has(t.key)).map(t => t.key));
      })
      .catch(() => {
        if (active) setUnseenKeys([]);
      });
    return () => { active = false; };
  }, []);

  if (!unseenKeys || unseenKeys.length === 0) return null;

  const tip = SORTED_TIPS.find(t => t.key === unseenKeys[cursor % unseenKeys.length])!;

  const dismiss = () => {
    const dismissedKey = tip.key;
    markFeatureSeen(dismissedKey).catch(() => { /* best-effort; will resurface next load */ });
    setUnseenKeys(keys => (keys ?? []).filter(k => k !== dismissedKey));
    setCursor(0);
  };

  return (
    <div className='d-flex align-items-start gap-3 bg-light-warning rounded px-4 py-3 w-100'>
      <div className='w-32px h-32px flex-shrink-0 rounded-2 d-flex align-items-center justify-content-center bg-warning'>
        <KTIcon iconName={tip.icon} className='text-white fs-5' />
      </div>
      <div className='flex-grow-1 min-w-0'>
        <div className='d-flex align-items-center gap-2 mb-1'>
          <span className='badge badge-light-warning text-warning fs-9 py-1 px-2'>{"🆕 What's New".toTranslatedDisplay()}</span>
          <span className='fw-bold text-gray-900 fs-7'>{tip.title.toTranslatedDisplay()}</span>
        </div>
        <p className='text-muted fs-8 mb-0 lh-base'>{tip.body.toTranslatedDisplay()}</p>
      </div>
      <div className='d-flex gap-2 flex-shrink-0 pt-1'>
        {unseenKeys.length > 1 && (
          <button
            className='btn btn-sm btn-light-warning py-1 px-2 fs-8'
            onClick={() => setCursor(c => (c + 1) % unseenKeys.length)}
          >
            {'Next →'.toTranslatedDisplay()}
          </button>
        )}
        <button
          className='btn btn-sm btn-light py-1 px-2 fs-8'
          onClick={dismiss}
        >
          {'✕'.toTranslatedDisplay()}
        </button>
      </div>
    </div>
  );
};

// ── ComparisonKpiCard ─────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string;
  tooltip?: string;
  icon: string;
  current: number;
  prior: number;
  color: string;
  higherIsBetter?: boolean;
  isSuffix?: boolean;
  suffix?: string;
  onClick?: () => void;
}

const ComparisonKpiCard: FC<KpiCardProps> = ({
  title, tooltip, icon, current, prior, color,
  higherIsBetter = true, isSuffix = false, suffix = '%', onClick,
}) => {
  useBootstrapTooltips([tooltip]);
  const change = pctChange(current, prior);
  const isPositive = higherIsBetter ? change >= 0 : change <= 0;
  const formatted = isSuffix ? `${current.toFixed(2)}${suffix}` : formatCurrency(current);
  const priorFormatted = isSuffix ? `${prior.toFixed(2)}${suffix}` : formatCurrency(prior);

  return (
    <div
      className={`card h-100${onClick ? ' cursor-pointer' : ''}`}
      style={{ borderLeft: `4px solid var(--bs-${color})` }}
      onClick={onClick}
    >
      <div className='card-body p-6'>
        <div className='d-flex align-items-center gap-3 mb-5'>
          <div
            className={`w-36px h-36px rounded-2 d-flex align-items-center justify-content-center bg-light-${color} flex-shrink-0`}
          >
            <KTIcon iconName={icon} className={`text-${color} fs-3`} />
          </div>
          <span className='text-muted fw-semibold fs-8 text-uppercase ls-1'>{title}</span>
          {tooltip && (
            <i
              className='bi bi-question-circle text-muted fs-8'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              title={tooltip.toTranslatedDisplay()}
              style={{ cursor: 'pointer' }}
              onClick={e => e.stopPropagation()}
            ></i>
          )}
        </div>

        <div className='d-flex align-items-baseline gap-3 mb-3 flex-wrap'>
          <span className='fw-bold text-gray-900' style={{ fontSize: '1.55rem', lineHeight: 1.15 }}>
            {formatted}
          </span>
          <span className={`badge badge-light-${isPositive ? 'success' : 'danger'} fs-8 px-2 py-1`}>
            {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}%
          </span>
        </div>

        <div className='text-muted fs-8'>{'vs'.toTranslatedDisplay()} {priorFormatted} {'prior period'.toTranslatedDisplay()}</div>
      </div>
    </div>
  );
};

// ── PLComparisonChart ─────────────────────────────────────────────────────────

interface PLChartProps {
  current: PeriodData;
  prior: PeriodData;
  currentLabel: string;
  priorLabel: string;
}

const PLComparisonChart: FC<PLChartProps> = ({
  current, prior, currentLabel, priorLabel,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();

  const buildOptions = useCallback((): ApexOptions => {
    const months = current.profitnLossData.map(d => d.period.slice(0, 3));
    const borderColor = getCSSVariableValue('--bs-gray-200');
    const labelColor = getCSSVariableValue('--bs-gray-500');

    return {
      series: [
        { name: 'Income'.toTranslatedDisplay(), type: 'bar', data: current.profitnLossData.map(d => d.income) },
        { name: 'Expense'.toTranslatedDisplay(), type: 'bar', data: current.profitnLossData.map(d => d.expense) },
        { name: 'Income (Prior)'.toTranslatedDisplay(), type: 'line', data: prior.profitnLossData.map(d => d.income) },
        { name: 'Expense (Prior)'.toTranslatedDisplay(), type: 'line', data: prior.profitnLossData.map(d => d.expense) },
      ],
      chart: { type: 'line', height: 300, toolbar: { show: false }, fontFamily: 'inherit' },
      colors: ['#22C55E', '#EF4444', '#86EFAC', '#FCA5A5'],
      stroke: { width: [0, 0, 2, 2], dashArray: [0, 0, 5, 5], curve: 'smooth' },
      plotOptions: { bar: { columnWidth: '40%', borderRadius: 3 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: months,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: '12px', colors: labelColor } },
      },
      yaxis: {
        labels: {
          formatter: fmtCompact,
          style: { fontSize: '11px', colors: labelColor },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        markers: { size: 8 },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (v: number) => formatCurrency(v) },
      },
      grid: { strokeDashArray: 4, borderColor },
    };
  }, [current, prior, mode]);

  useEffect(() => {
    if (!chartRef.current || !current.profitnLossData.length) return;
    const chart = new ApexCharts(chartRef.current, buildOptions());
    chart.render();
    return () => chart.destroy();
  }, [buildOptions]);

  return (
    <div className='card h-100'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{'Profit & Loss'.toTranslatedDisplay()}</span>
          <span className='text-muted fw-semibold fs-7'>
            {currentLabel}
            <span className='text-gray-300 mx-2'>{'vs'.toTranslatedDisplay()}</span>
            {priorLabel}
          </span>
        </h3>
      </div>
      <div className='px-5 pb-5'>
        {current.profitnLossData.length === 0 ? (
          <div
            className='d-flex align-items-center justify-content-center text-muted fs-6'
            style={{ height: '300px' }}
          >
            {'No data for this period'.toTranslatedDisplay()}
          </div>
        ) : (
          <div ref={chartRef} style={{ height: '300px' }} />
        )}
      </div>
    </div>
  );
};

// ── CashFlowComparisonChart ───────────────────────────────────────────────────

interface CashFlowChartProps {
  current: PeriodData;
  prior: PeriodData;
  currentLabel: string;
  priorLabel: string;
}

const CashFlowComparisonChart: FC<CashFlowChartProps> = ({
  current, prior, currentLabel, priorLabel,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();

  const buildOptions = useCallback((): ApexOptions => {
    const months = current.cashFlowData.map(d => d.period.slice(0, 3));
    const primary = getCSSVariableValue('--bs-primary');
    const gray = getCSSVariableValue('--bs-gray-400');
    const borderColor = getCSSVariableValue('--bs-gray-200');
    const labelColor = getCSSVariableValue('--bs-gray-500');

    return {
      series: [
        { name: 'Current Period'.toTranslatedDisplay(), data: current.cashFlowData.map(d => d.balance) },
        { name: 'Prior Period'.toTranslatedDisplay(), data: prior.cashFlowData.map(d => d.balance) },
      ],
      chart: { type: 'line', height: 300, toolbar: { show: false }, fontFamily: 'inherit' },
      colors: [primary, gray],
      stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 6] },
      dataLabels: { enabled: false },
      xaxis: {
        categories: months,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: '12px', colors: labelColor } },
      },
      yaxis: {
        labels: {
          formatter: fmtCompact,
          style: { fontSize: '11px', colors: labelColor },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        markers: { size: 8 },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (v: number) => formatCurrency(v) },
      },
      grid: { strokeDashArray: 4, borderColor },
      markers: { size: [4, 3], strokeWidth: 2 },
    };
  }, [current, prior, mode]);

  useEffect(() => {
    if (!chartRef.current || !current.cashFlowData.length) return;
    const chart = new ApexCharts(chartRef.current, buildOptions());
    chart.render();
    return () => chart.destroy();
  }, [buildOptions]);

  return (
    <div className='card h-100'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{'Cash Flow'.toTranslatedDisplay()}</span>
          <span className='text-muted fw-semibold fs-7'>
            {'Cumulative net cash'.toTranslatedDisplay()} — {currentLabel}
            <span className='text-gray-300 mx-2'>{'vs'.toTranslatedDisplay()}</span>
            {priorLabel}
          </span>
        </h3>
      </div>
      <div className='px-5 pb-5 flex-grow-1 d-flex flex-column'>
        {current.cashFlowData.length === 0 ? (
          <div
            className='d-flex align-items-center justify-content-center text-muted fs-6 flex-grow-1'
            style={{ minHeight: '300px' }}
          >
            {'No data for this period'.toTranslatedDisplay()}
          </div>
        ) : (
          <div ref={chartRef} style={{ height: '300px' }} />
        )}
      </div>
    </div>
  );
};

// ── BalancePanel ──────────────────────────────────────────────────────────────

const BALANCE_ROWS = [
  { key: 'bankBalance' as const,    label: 'Bank Balance',    subtitle: 'All bank accounts',           icon: 'bank',        color: 'primary', bg: 'light-primary', path: '/reports/bank-statement',
    tooltip: 'Sum of all bank account balances as recorded in your bank/connected accounts.' },
  { key: 'myBooksBalance' as const, label: 'myBooks Balance', subtitle: 'General ledger balance',      icon: 'abstract-26', color: 'info',    bg: 'light-info',    path: '/reports/trial-balance',
    tooltip: 'The bank balance as calculated from your recorded transactions (general ledger). This may differ from Bank Balance if some transactions haven\'t been reconciled yet.' },
  { key: 'cashBalance' as const,    label: 'Cash Balance',    subtitle: 'Petty cash & cash in hand',   icon: 'wallet',      color: 'success', bg: 'light-success', path: '/reports/account-transactions',
    tooltip: 'Balance held in petty cash / cash-in-hand accounts, separate from bank accounts.' },
  { key: 'overdue' as const,        label: 'Overdue (AR)',    subtitle: 'Unpaid customer invoices',    icon: 'time',        color: 'danger',  bg: 'light-danger',  path: '/reports/customer-aging-summary',
    tooltip: 'Total of unpaid customer invoices that are past their due date.' },
];

const BalancePanel: FC<{ balances: BalancesDTO }> = ({ balances }) => {
  const navigate = useNavigate();
  useBootstrapTooltips([balances]);
  return (
    <div className='card h-100'>
      <div className='card-header border-0 pt-6 pb-4'>
        <div>
          <div className='fw-bold fs-3 text-gray-900 mb-1'>{'Balances'.toTranslatedDisplay()}</div>
          <span className='text-muted fw-semibold fs-7'>{'Live balance-sheet totals'.toTranslatedDisplay()}</span>
        </div>
      </div>
      <div className='card-body d-flex flex-column justify-content-evenly pt-0 pb-4'>
        {BALANCE_ROWS.map((row, i) => {
          const value = balances[row.key];
          const amountColor = row.key === 'overdue' || value < 0 ? 'danger' : 'gray-800';
          return (
            <div key={row.key}>
              <div
                className='d-flex align-items-center gap-4 py-4 cursor-pointer'
                onClick={() => navigate(row.path)}
              >
                <div
                  className={`w-50px h-50px flex-shrink-0 rounded-2 d-flex align-items-center justify-content-center bg-${row.bg}`}
                >
                  <KTIcon iconName={row.icon} className={`text-${row.color} fs-1`} />
                </div>
                <div className='flex-grow-1 d-flex align-items-center justify-content-between min-w-0'>
                  <div>
                    <div className='fw-bold text-gray-800 fs-6 lh-sm d-flex align-items-center gap-2'>
                      {row.label.toTranslatedDisplay()}
                      <i
                        className='bi bi-question-circle text-muted fs-8'
                        data-bs-toggle='tooltip'
                        data-bs-placement='top'
                        title={row.tooltip.toTranslatedDisplay()}
                        style={{ cursor: 'pointer' }}
                        onClick={e => e.stopPropagation()}
                      ></i>
                    </div>
                    <div className='text-muted fs-8'>{row.subtitle.toTranslatedDisplay()}</div>
                  </div>
                  <span className={`fw-bolder fs-4 text-${amountColor} ms-3 flex-shrink-0`}>
                    {formatCurrency(value)}
                  </span>
                </div>
              </div>
              {i < BALANCE_ROWS.length - 1 && <div className='separator separator-dashed' />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── SubscriptionNotice ────────────────────────────────────────────────────────

const LOCKED_FEATURES_ALL = [
  { icon: 'bank',           label: 'Banking & Reconciliation', minEdition: 'Standard' },
  { icon: 'graph-up',       label: 'Advanced Reports',         minEdition: 'Standard' },
  { icon: 'archive-tick',   label: 'Backup & Restore',         minEdition: 'Standard' },
  { icon: 'abstract-26',    label: 'Multi-Branch Support',     minEdition: 'Premium'  },
  { icon: 'message-text-2', label: 'AI Accounting Assistant',  minEdition: 'Premium'  },
];

const SubscriptionNotice: FC<{ subscription: SubscriptionData }> = ({ subscription }) => {
  const navigate = useNavigate();
  const plan = subscription?.currentPlan;
  if (!plan) return null;

  const effectiveEdition = getEffectiveEdition(plan.edition, plan.status, plan.isTrial);
  const isExpiredOrCancelled = plan.status === 'expired' || plan.status === 'cancelled' || plan.status === 'inactive';
  const isFree = effectiveEdition === 'Free';
  const goToSubscription = () => navigate('/settings/subscription');

  // ── 1. Trial countdown — compact alert only ──────────────────────────────
  if (plan.isTrial && (plan.trialEndsAt || plan.endDate)) {
    const trialEnd = plan.trialEndsAt ?? plan.endDate;
    const ms = new Date(trialEnd).getTime() - Date.now();
    const trialDaysLeft = Math.ceil(ms / 86_400_000);
    const isUrgent = trialDaysLeft <= 3;
    return (
      <div
        className={`alert alert-${isUrgent ? 'danger' : 'info'} d-flex align-items-center justify-content-between py-4 mb-6 gap-4`}
        role='alert'
      >
        <div className='d-flex align-items-center gap-3'>
          <i className={`fas ${isUrgent ? 'fa-exclamation-circle' : 'fa-hourglass-half'} fs-3`} />
          <span className='fw-semibold'>
            {trialDaysLeft > 0
              ? `${'Trial ends in'.toTranslatedDisplay()} ${trialDaysLeft} ${trialDaysLeft === 1 ? 'day'.toTranslatedDisplay() : 'days'.toTranslatedDisplay()}. ${'Subscribe now to keep full access.'.toTranslatedDisplay()}`
              : 'Your trial has ended. Subscribe now to continue.'.toTranslatedDisplay()
            }
          </span>
        </div>
        <button
          className={`btn btn-sm btn-${isUrgent ? 'danger' : 'primary'} flex-shrink-0`}
          onClick={goToSubscription}
        >
          {'Subscribe Now'.toTranslatedDisplay()}
        </button>
      </div>
    );
  }

  // ── 2. Expired / cancelled or Free — full conversion card ────────────────
  if (isExpiredOrCancelled || isFree) {
    const lockedFeatures = LOCKED_FEATURES_ALL.filter(f => {
      if (effectiveEdition === 'Standard') return ['Premium', 'Enterprise'].includes(f.minEdition);
      return true;
    });

    return (
      <div className='card border border-dashed border-primary mb-6'>
        <div className='card-body p-8'>
          <div className='row align-items-center g-6'>
            <div className='col-lg-7'>
              <div className='d-flex align-items-center gap-3 mb-4'>
                <div className='w-48px h-48px rounded-2 bg-light-primary d-flex align-items-center justify-content-center flex-shrink-0'>
                  <KTIcon iconName='crown' className='text-primary fs-1' />
                </div>
                <div>
                  <h4 className='fw-bold text-gray-900 mb-1'>
                    {isExpiredOrCancelled
                      ? 'Renew Your Subscription'.toTranslatedDisplay()
                      : 'Unlock Full Access'.toTranslatedDisplay()
                    }
                  </h4>
                  <p className='text-muted fs-7 mb-0'>
                    {isExpiredOrCancelled
                      ? 'Your plan has expired. Renew now to restore all features.'.toTranslatedDisplay()
                      : 'Upgrade your plan to unlock powerful accounting features.'.toTranslatedDisplay()
                    }
                  </p>
                </div>
              </div>
              <div className='d-flex flex-wrap gap-3'>
                {lockedFeatures.map(f => (
                  <div key={f.label} className='d-flex align-items-center gap-2 bg-light rounded px-3 py-2'>
                    <i className='bi bi-lock-fill text-warning' style={{ fontSize: '11px' }} />
                    <span className='fs-8 fw-semibold text-gray-700'>{f.label.toTranslatedDisplay()}</span>
                    <span className='badge badge-light-primary' style={{ fontSize: '8px' }}>{f.minEdition}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='col-lg-5 text-center text-lg-end'>
              <button
                className='btn btn-primary btn-lg px-8'
                onClick={goToSubscription}
              >
                <KTIcon iconName='rocket' className='me-2' />
                {isExpiredOrCancelled ? 'Renew Now'.toTranslatedDisplay() : 'Upgrade Now'.toTranslatedDisplay()}
              </button>
              <div className='text-muted fs-8 mt-2'>
                {'Plans start from just'.toTranslatedDisplay()} $12/{'month'.toTranslatedDisplay()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── 3. Paid plan — only warn about expiry when autoRenew is off ──────────
  if (!plan.autoRenew && plan.endDate) {
    const ms = new Date(plan.endDate).getTime() - Date.now();
    const daysLeft = isNaN(ms) ? null : Math.ceil(ms / 86_400_000);

    if (daysLeft !== null && daysLeft <= 7) {
      return (
        <div
          className='alert alert-danger d-flex align-items-center justify-content-between py-4 mb-6 gap-4'
          role='alert'
        >
          <div className='d-flex align-items-center gap-3'>
            <i className='fas fa-exclamation-circle text-danger fs-3' />
            <span className='fw-semibold'>
              {`${'Your subscription expires in'.toTranslatedDisplay()} ${daysLeft} ${daysLeft === 1 ? 'day'.toTranslatedDisplay() : 'days'.toTranslatedDisplay()}. ${'Renew now to avoid interruption.'.toTranslatedDisplay()}`}
            </span>
          </div>
          <button className='btn btn-sm btn-danger flex-shrink-0' onClick={goToSubscription}>
            {'Renew Now'.toTranslatedDisplay()}
          </button>
        </div>
      );
    }

    if (daysLeft !== null && daysLeft <= 30) {
      return (
        <div
          className='alert alert-warning d-flex align-items-center justify-content-between py-4 mb-6 gap-4'
          role='alert'
        >
          <div className='d-flex align-items-center gap-3'>
            <i className='fas fa-clock text-warning fs-3' />
            <span className='fw-semibold'>
              {`${'Your subscription expires in'.toTranslatedDisplay()} ${daysLeft} ${'days'.toTranslatedDisplay()}. ${'Renew now to keep full access.'.toTranslatedDisplay()}`}
            </span>
          </div>
          <button className='btn btn-sm btn-warning flex-shrink-0' onClick={goToSubscription}>
            {'Renew Now'.toTranslatedDisplay()}
          </button>
        </div>
      );
    }
  }

  return null;
};

// ── DashboardHeader ───────────────────────────────────────────────────────────

interface DashboardHeaderProps {
  currentLabel: string;
  priorLabel: string;
  selectedPeriod: string;
  onPeriodChange: (v: string) => void;
  loading: boolean;
}

const DashboardHeader: FC<DashboardHeaderProps> = ({
  currentLabel, priorLabel, selectedPeriod, onPeriodChange, loading,
}) => (
  <div className='mb-7'>
    {/* Row 1: title + period picker on left, tip on right */}
    <div className='d-flex align-items-start justify-content-between flex-wrap gap-3 mb-2'>
      {/* Left: title + period labels + period picker */}
      <div>
        <div className='d-flex align-items-center gap-3 mb-2'>
          <h2 className='fw-bold text-gray-900 mb-0'>{'Dashboard'.toTranslatedDisplay()}</h2>
          <PeriodSelect value={selectedPeriod} onChange={onPeriodChange} loading={loading} />
        </div>
        <div className='d-flex align-items-center gap-3 text-muted fs-7 flex-wrap'>
          <span>
            <span className='fw-semibold text-gray-700'>{'Current:'.toTranslatedDisplay()}</span> {currentLabel}
          </span>
          <span className='text-gray-300'>|</span>
          <span>
            <span className='fw-semibold text-gray-700'>{'Prior:'.toTranslatedDisplay()}</span> {priorLabel}
          </span>
        </div>
      </div>
      {/* Right: compact tip occupying ~50% width */}
      <div style={{ width: '50%', minWidth: '280px' }}>
        <CompactTip />
      </div>
    </div>
  </div>
);

// ── DashboardPageV2 (display) ─────────────────────────────────────────────────

interface DashboardPageV2Props {
  data: DashboardResponseV2 | undefined;
  subscription: SubscriptionData | undefined;
  selectedPeriod: string;
  onPeriodChange: (v: string) => void;
  loading: boolean;
}

const WELCOME_NOTICE_KEY = 'welcomeNotice-4';

const DashboardPageV2: FC<DashboardPageV2Props> = ({
  data, subscription, selectedPeriod, onPeriodChange, loading,
}) => {
  const navigate = useNavigate();
  const { bizSettings } = useAppContext();
  const [welcomeNotice, setWelcomeNotice] = useState(() => {
    const saved = localStorage.getItem(WELCOME_NOTICE_KEY);
    return saved === null ? true : JSON.parse(saved);
  });

  useEffect(() => {
    localStorage.setItem(WELCOME_NOTICE_KEY, JSON.stringify(welcomeNotice));
  }, [welcomeNotice]);

  const currentLabel = formatPeriodLabel(
    data?.current.fromDate ?? '',
    data?.current.toDate ?? ''
  );
  const priorLabel = formatPeriodLabel(
    data?.prior.fromDate ?? '',
    data?.prior.toDate ?? ''
  );

  const empty: PeriodData = {
    fromDate: '', toDate: '',
    profitnLossData: [], cashFlowData: [], expenseCategories: [],
    kpis: { sales: 0, expense: 0, operatingPM: 0, netProfit: 0 },
  };

  const current = data?.current ?? empty;
  const prior = data?.prior ?? empty;
  const balances = data?.balances ?? { bankBalance: 0, myBooksBalance: 0, cashBalance: 0, overdue: 0 };

  // KPI card drilldown: carry the dashboard's active period so the target report
  // lands filtered and auto-run instead of a blank filter panel.
  const drilldownQuery = (from: string | Date, to: string | Date) => {
    const f = from instanceof Date ? from.toISOString() : from;
    const t = to instanceof Date ? to.toISOString() : to;
    return `from=${encodeURIComponent(f)}&to=${encodeURIComponent(t)}&predefinedDateFilter=${encodeURIComponent(selectedPeriod)}`;
  };

  const donutSeries = current.expenseCategories.map(c => c.amount);
  const donutLabels = current.expenseCategories.map(c => c.expenseCategory);

  return (
    <>
      <ToolbarWrapper />
      <Content>
        <div className='container-xxl'>
          <OnboardingHelper className='mb-8' />

          {/* Header: title, period labels, compact tip, period filter */}
          <DashboardHeader
            currentLabel={currentLabel}
            priorLabel={priorLabel}
            selectedPeriod={selectedPeriod}
            onPeriodChange={onPeriodChange}
            loading={loading}
          />

          {/* Subscription notice */}
          {brandSettings.showSubscription && subscription && (
            <SubscriptionNotice subscription={subscription} />
          )}

          {/* ── Row 1: 4 KPI cards ── */}
          <div className='row g-4 mb-6'>
            <div className='col-6 col-xl-3'>
              <ComparisonKpiCard
                title={'Sales'.toTranslatedDisplay()}
                tooltip={'Total invoiced sales for the selected period, compared to the prior period.'}
                icon='dollar'
                current={current.kpis.sales}
                prior={prior.kpis.sales}
                color='primary'
                onClick={() => navigate(`/reports/invoice-report?${drilldownQuery(current.fromDate, current.toDate)}`)}
              />
            </div>
            <div className='col-6 col-xl-3'>
              <ComparisonKpiCard
                title={'Expenses'.toTranslatedDisplay()}
                tooltip={'Total recorded expenses for the selected period, compared to the prior period.'}
                icon='minus-circle'
                current={current.kpis.expense}
                prior={prior.kpis.expense}
                color='danger'
                higherIsBetter={false}
                onClick={() => navigate(`/reports/expense-report?${drilldownQuery(current.fromDate, current.toDate)}`)}
              />
            </div>
            <div className='col-6 col-xl-3'>
              <ComparisonKpiCard
                title={'Net Profit'.toTranslatedDisplay()}
                tooltip={'Sales minus expenses for the selected period (before tax).'}
                icon='chart-line-up'
                current={current.kpis.netProfit}
                prior={prior.kpis.netProfit}
                color='success'
                onClick={() => navigate(`/reports/profit-loss?${drilldownQuery(current.fromDate, current.toDate)}`)}
              />
            </div>
            <div className='col-6 col-xl-3'>
              <ComparisonKpiCard
                title={'Profit Margin'.toTranslatedDisplay()}
                tooltip={'Net profit as a percentage of sales for the selected period.'}
                icon='percentage'
                current={current.kpis.operatingPM}
                prior={prior.kpis.operatingPM}
                color='info'
                isSuffix
                suffix='%'
                onClick={() => navigate(`/reports/profit-loss?${drilldownQuery(current.fromDate, current.toDate)}`)}
              />
            </div>
          </div>

          {/* ── AI Status Brief (Premium+) ── */}
          <AiStatusBriefCard locked={!canUseModule('AIAssistant', getEffectiveEdition(
            subscription?.currentPlan?.edition ?? 'Free',
            subscription?.currentPlan?.status ?? 'inactive',
            subscription?.currentPlan?.isTrial ?? false,
          ))} />

          {/* ── Row 2: P&L chart + Balance panel ── */}
          <div className='row g-5 mb-6'>
            <div className='col-xl-8'>
              <PLComparisonChart
                current={current}
                prior={prior}
                currentLabel={currentLabel}
                priorLabel={priorLabel}
              />
            </div>
            <div className='col-xl-4'>
              <BalancePanel balances={balances} />
            </div>
          </div>

          {/* ── Row 3: Cash Flow chart + Expense Donut ── */}
          <div className='row g-5 mb-6'>
            <div className='col-xl-8'>
              <CashFlowComparisonChart
                current={current}
                prior={prior}
                currentLabel={currentLabel}
                priorLabel={priorLabel}
              />
            </div>
            <div className='col-xl-4'>
              <DonutChart
                series={donutSeries}
                labels={donutLabels}
                className='h-100'
                chartHeight='300px'
              />
            </div>
          </div>

          {/* ── Budget Tracker widget (self-hides if no active budget or no access) ── */}
          {bizSettings?.budgetSettings?.enabled && canUseModule('Budgets', getEffectiveEdition(
            subscription?.currentPlan?.edition ?? 'Free',
            subscription?.currentPlan?.status ?? 'inactive',
            subscription?.currentPlan?.isTrial ?? false,
          )) && (
            <div className='row g-5 mb-5'>
              <div className='col-12 col-lg-6'>
                <BudgetTrackerWidget />
              </div>
            </div>
          )}

          {/* ── Marketing widget ── */}
          {brandSettings.showMarketingWidget && (
            <div className='row g-5'>
              <div className='col-12'>
                <MarketingWidget className='card-xl-stretch mb-5 mb-xl-8' />
              </div>
            </div>
          )}
        </div>
      </Content>

      {brandSettings.showForMainBrand && (
        <CustomModal
          id={''}
          title={`${"What's New in".toTranslatedDisplay()} ${brandSettings.displayName}!`}
          body={
            <>
              <div className='d-flex align-items-center alert alert-success' role='alert'>
                <i className="bi bi-check-circle-fill fs-3 me-3 flex-shrink-0"></i>
                <span>
                  {'Thank you for choosing'.toTranslatedDisplay()} {brandSettings.displayName}{'! Here\'s what\'s new in this release.'.toTranslatedDisplay()}
                </span>
              </div>

              <div className="mt-3">
                <h6 className="fw-bold mb-3">{'🚀 Latest:'.toTranslatedDisplay()}</h6>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3 p-3 bg-light-primary rounded">
                      <i className="bi bi-robot text-primary fs-3 flex-shrink-0"></i>
                      <div>
                        <div className="fw-bold text-gray-800 fs-7">{'AI-Powered Insights'.toTranslatedDisplay()}</div>
                        <div className="text-muted fs-8">{'AI status briefs, report insights, bank reconciliation suggestions and invoice scanning.'.toTranslatedDisplay()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3 p-3 bg-light-success rounded">
                      <i className="bi bi-credit-card text-success fs-3 flex-shrink-0"></i>
                      <div>
                        <div className="fw-bold text-gray-800 fs-7">{'Payment Gateway Integration'.toTranslatedDisplay()}</div>
                        <div className="text-muted fs-8">{'Accept payments via Razorpay, PhonePe or Stripe Connect.'.toTranslatedDisplay()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3 p-3 bg-light-info rounded">
                      <i className="bi bi-person-badge text-info fs-3 flex-shrink-0"></i>
                      <div>
                        <div className="fw-bold text-gray-800 fs-7">{'Customer Portal'.toTranslatedDisplay()}</div>
                        <div className="text-muted fs-8">{'Give customers a self-service portal to view invoices and statements.'.toTranslatedDisplay()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3 p-3 bg-light-warning rounded">
                      <i className="bi bi-gear-wide-connected text-warning fs-3 flex-shrink-0"></i>
                      <div>
                        <div className="fw-bold text-gray-800 fs-7">{'Production / BOM Module'.toTranslatedDisplay()}</div>
                        <div className="text-muted fs-8">{'Build production orders from a Bill of Materials and track output.'.toTranslatedDisplay()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3 p-3 bg-light-danger rounded">
                      <i className="bi bi-graph-up-arrow text-danger fs-3 flex-shrink-0"></i>
                      <div>
                        <div className="fw-bold text-gray-800 fs-7">{'Invoice Profitability Report'.toTranslatedDisplay()}</div>
                        <div className="text-muted fs-8">{'See margin per invoice, broken down by item and customer.'.toTranslatedDisplay()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3 p-3 bg-light-primary rounded">
                      <i className="bi bi-clock-history text-primary fs-3 flex-shrink-0"></i>
                      <div>
                        <div className="fw-bold text-gray-800 fs-7">{'Audit Log'.toTranslatedDisplay()}</div>
                        <div className="text-muted fs-8">{'Full create and change history for every record, with who changed what and when.'.toTranslatedDisplay()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3 p-3 bg-light-success rounded">
                      <i className="bi bi-lock text-success fs-3 flex-shrink-0"></i>
                      <div>
                        <div className="fw-bold text-gray-800 fs-7">{'Financial Year Lock'.toTranslatedDisplay()}</div>
                        <div className="text-muted fs-8">{'Lock prior-period transactions so closed books can no longer be edited.'.toTranslatedDisplay()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3 p-3 bg-light-info rounded">
                      <i className="bi bi-file-earmark-text text-info fs-3 flex-shrink-0"></i>
                      <div>
                        <div className="fw-bold text-gray-800 fs-7">{'New Print Templates & Layouts'.toTranslatedDisplay()}</div>
                        <div className="text-muted fs-8">{'Portrait/landscape orientation, A5/Letter sizing, and redesigned invoice templates.'.toTranslatedDisplay()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-light rounded">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  {'Need help? Check out our'.toTranslatedDisplay()}{' '}
                  <a target="_blank" href="https://mybooksai.app/help/" className="text-decoration-none">
                    {'documentation'.toTranslatedDisplay()}
                  </a>{' '}
                  {'or contact our support team.'.toTranslatedDisplay()}
                </small>
              </div>
            </>
          }
          isVisible={welcomeNotice}
          onClose={() => setWelcomeNotice(false)}
        />
      )}
    </>
  );
};

// ── QuickAccessDashboard ──────────────────────────────────────────────────────

const QuickAccessDashboard: FC = () => (
  <>
    <ToolbarWrapper />
    <Content>
      <div className='container-xxl'>
        <OnboardingHelper className='mb-8' />
        <div className='mb-8'>
          <h2 className='fw-bold text-gray-900 mb-2'>{'Quick Access'.toTranslatedDisplay()}</h2>
          <p className='text-muted fs-6'>
            {'Select a module below to get started.'.toTranslatedDisplay()}
          </p>
        </div>
        <AccessibleTiles />
      </div>
    </Content>
  </>
);

// ── DashboardWrapperV2 (data fetching) ────────────────────────────────────────

const DashboardWrapperV2: FC = () => {
  const { updateToolbar } = useContext(ToolbarContext);
  const { roleAccess, subscriptionData } = useAppContext();
  const [selectedPeriod, setSelectedPeriod] = useState('ThisYear');
  const [data, setData] = useState<DashboardResponseV2 | undefined>();
  const [loading, setLoading] = useState(false);

  const hasFinancialAccess =
    roleAccess?.isAdmin === true ||
    canAccess(roleAccess?.permissions ?? [], 'FinancialReport', 'read');

  const cachePrefix = `dashV2_${globalValue?.bizMaster?.bizId ?? ''}`;

  const loadDashboard = useCallback(
    async (period: string) => {
      setLoading(true);
      const key = `${cachePrefix}_${period}`;
      const cached = localStorage.getItem(key);
      if (cached) {
        try {
          setData(JSON.parse(cached));
        } catch { /* stale / corrupt cache */ }
      }
      try {
        // Called sequentially, not via Promise.all — firing all three concurrently was measured
        // to push getDashboardBalances past API Gateway's 29s timeout even on the deployed
        // Lambda. See project-docs/dashboard-performance-tuning.md (mybooks-backend) for why.
        const reportFilter = buildReportFilter(period);
        const current = await fetchDashboardCurrentPeriod(reportFilter, true);
        const prior = await fetchDashboardPriorPeriod(reportFilter, true);
        const balances = await fetchDashboardBalances(reportFilter, true);
        if (current && prior && balances) {
          const result: DashboardResponseV2 = { current, prior, balances };
          setData(result);
          localStorage.setItem(key, JSON.stringify(result));
        }
      } catch (e) {
        console.error('Dashboard v2 fetch error:', e);
      } finally {
        setLoading(false);
      }
    },
    [cachePrefix]
  );

  useEffect(() => {
    updateToolbar({
      ...defaultToolbarState,
      alwaysDisplay: true,
      showBranchPicker: true,
      showAIChat: true,
    });
    fetchItems(true);
    fetchContacts(true, true);
    fetchAccounts(true);
  }, []);

  useEffect(() => {
    if (hasFinancialAccess) {
      loadDashboard(selectedPeriod);
    }
  }, [selectedPeriod, loadDashboard, hasFinancialAccess]);

  return (
    <>
      <PageTitle showBizPicker />
      {hasFinancialAccess ? (
        <DashboardPageV2
          data={data}
          subscription={subscriptionData}
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          loading={loading}
        />
      ) : (
        <QuickAccessDashboard />
      )}
    </>
  );
};

export { DashboardWrapperV2 };
